/**
 * UserCombobox — searchable user selector with server-side filtering by name, email or phone.
 * Mirrors TenantCombobox; used where the operator starts from a person rather than a tenant.
 */

import { useState, useEffect } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useUsers } from "@/hooks/useUserQueries";
import type { User } from "@/types/admin";
import { cn } from "@/lib/utils";

export function UserCombobox({
  id,
  value,
  onChange,
  disabled,
}: {
  /** Id of the trigger, so a `<Label htmlFor>` can address the control. */
  id?: string;
  value: string;
  onChange: (user: User | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  // The chosen user is remembered locally: the next search page may not contain them, and the
  // trigger must keep showing who is selected rather than falling back to a placeholder.
  const [picked, setPicked] = useState<User | null>(null);

  // 750ms rather than the usual 300: the admin user list is large and unindexed on name/email, so
  // a shorter window fires a query on nearly every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 750);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isFetching } = useUsers({
    search: debounced || undefined,
    page: 1,
    pageSize: 50,
  });
  const users = data?.data ?? [];
  const selected =
    users.find((u) => u.id === value) ??
    (picked && picked.id === value ? picked : null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            // This trigger *is* the user field, so it follows the flat form-control contract
            // (docs/design/frontend-form-controls.md) instead of the raised outline button.
            "h-9 w-full justify-between rounded-md border-input px-3 font-normal shadow-none",
            "hover:translate-y-0 hover:border-input hover:bg-transparent",
            !selected && "text-placeholder",
          )}
        >
          <span className="truncate">
            {selected
              ? `${selected.fullName} (${selected.email})`
              : "Search user by name, email or phone…"}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        <div className="border-b p-2">
          <Input
            placeholder="Search users by name, email or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-auto p-1">
          {isFetching && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Loading…
            </div>
          )}
          {!isFetching && users.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No users found.
            </div>
          )}
          {users.map((u) => (
            <button
              key={u.id}
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-accent",
                u.id === value && "bg-accent",
              )}
              onClick={() => {
                setPicked(u);
                onChange(u);
                setOpen(false);
                setSearch("");
              }}
            >
              <span className="min-w-0 truncate">
                {u.fullName}
                <span className="ml-2 text-muted-foreground">{u.email}</span>
                {/* The phone is shown because it is searchable: after a phone search the row
                    has to say which number matched, or the operator cannot tell the hits apart. */}
                {u.phoneNumber && (
                  <span className="ml-2 text-muted-foreground">
                    {u.phoneNumber}
                  </span>
                )}
              </span>
              {u.id === value && <Check className="h-4 w-4 shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
